import React from 'react';
import {Breadcrumb, Card, message, Modal, Skeleton, theme, Typography} from 'antd';
import {HomeOutlined} from '@ant-design/icons';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {diagramsApi, foldersApi, projectsApi, type ProjectResponse} from '@/api/projects';
import {CreateDiagramModal, CreateFolderModal, CreateProjectModal, EditDiagramModal, EditFolderModal, EditProjectModal, ShareProjectModal} from '@components/dialog';
import WorkspaceSearch from '@/components/workspace/WorkspaceSearch';
import WorkspaceToolbar from '@/components/workspace/WorkspaceToolbar';
import WorkspaceTree from '@/components/workspace/WorkspaceTree';
import '@/components/workspace/WorkspaceSearch.css';
import {useWorkspaceTree} from '@/hooks/workspace';
import type {AccessGrantedState, WorkspaceSearchHit} from '@/types/api';
import type {WorkspaceDataNode} from '@/types/workspace/tree.types';
import {canDeleteProject, canEditContent, canManageProject, roleLabel} from '@/utils/workspace/projectRole';

const {Title, Text} = Typography;

const Workspace: React.FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const {
		token: {borderRadiusLG},
	} = theme.useToken();
	const tree = useWorkspaceTree();
	const fileInputRef = React.useRef<HTMLInputElement | null>(null);

	const [projectModalOpen, setProjectModalOpen] = React.useState(false);
	const [folderModalOpen, setFolderModalOpen] = React.useState(false);
	const [folderName, setFolderName] = React.useState('');
	const [folderLoading, setFolderLoading] = React.useState(false);
	const [diagramModalOpen, setDiagramModalOpen] = React.useState(false);
	const [diagramName, setDiagramName] = React.useState('');
	const [diagramLoading, setDiagramLoading] = React.useState(false);
	const [editFolderOpen, setEditFolderOpen] = React.useState(false);
	const [editFolderName, setEditFolderName] = React.useState('');
	const [editFolderLoading, setEditFolderLoading] = React.useState(false);
	const [editDiagramOpen, setEditDiagramOpen] = React.useState(false);
	const [editDiagramName, setEditDiagramName] = React.useState('');
	const [editDiagramLoading, setEditDiagramLoading] = React.useState(false);
	const [editProjectOpen, setEditProjectOpen] = React.useState(false);
	const [editProject, setEditProject] = React.useState<ProjectResponse | null>(null);
	const [importLoading, setImportLoading] = React.useState(false);
	const [deleting, setDeleting] = React.useState(false);
	const [shareOpen, setShareOpen] = React.useState(false);
	const [shareTarget, setShareTarget] = React.useState<WorkspaceDataNode | null>(null);
	const [accessNotice, setAccessNotice] = React.useState<AccessGrantedState | null>(null);

	React.useEffect(() => {
		const granted = (location.state as {accessGranted?: AccessGrantedState} | null)?.accessGranted;
		if (!granted) return;
		setAccessNotice(granted);
		navigate('.', {replace: true, state: {}});
	}, [location.state, navigate]);

	const selected = tree.selectedNode;

	const openDiagram = (node: WorkspaceDataNode) => {
		navigate(`/projects/${node.projectId}/diagrams/${node.entityId}`, {
			state: {diagramName: String(node.title ?? '')},
		});
	};

	const handleSearchSelect = (hit: WorkspaceSearchHit) => {
		if (hit.type === 'DIAGRAM') {
			navigate(`/projects/${hit.projectId}/diagrams/${hit.id}`, {
				state: {diagramName: hit.name},
			});
			return;
		}
		void tree.reveal(hit);
	};

	const handleCreateFolder = async () => {
		if (!folderName.trim()) {
			message.warning('Введите название папки');
			return;
		}
		setFolderLoading(true);
		const created = await tree.createFolder(folderName.trim());
		setFolderLoading(false);
		if (created) {
			setFolderModalOpen(false);
			setFolderName('');
		}
	};

	const handleCreateDiagram = async () => {
		if (!diagramName.trim()) {
			message.warning('Введите название диаграммы');
			return;
		}
		setDiagramLoading(true);
		const created = await tree.createDiagram(diagramName.trim());
		setDiagramLoading(false);
		if (created) {
			setDiagramModalOpen(false);
			setDiagramName('');
			navigate(`/projects/${created.projectId}/diagrams/${created.id}`, {
				state: {diagramName: created.name},
			});
		}
	};

	const handleEdit = () => {
		if (!selected) return;
		if (selected.nodeType === 'project') {
			if (!canManageProject(selected.role)) return;
			setEditProject({
				id: selected.projectId,
				name: String(selected.title ?? ''),
				description: selected.description ?? null,
				createdAt: '',
				role: selected.role,
				shared: selected.shared,
			});
			setEditProjectOpen(true);
			return;
		}
		if (!canEditContent(selected.role)) return;
		if (selected.nodeType === 'folder') {
			setEditFolderName(String(selected.title ?? ''));
			setEditFolderOpen(true);
			return;
		}
		setEditDiagramName(String(selected.title ?? ''));
		setEditDiagramOpen(true);
	};

	const handleSaveFolder = async () => {
		if (!selected || selected.nodeType !== 'folder' || !editFolderName.trim()) {
			message.warning('Введите название папки');
			return;
		}
		setEditFolderLoading(true);
		const {data, error} = await foldersApi.update(selected.projectId, selected.entityId, {
			name: editFolderName.trim(),
		});
		setEditFolderLoading(false);
		if (error || !data) {
			message.error(error?.message || 'Не удалось сохранить папку');
			return;
		}
		tree.renameSelected(data.name);
		setEditFolderOpen(false);
		message.success('Папка обновлена');
	};

	const handleSaveDiagram = async () => {
		if (!selected || selected.nodeType !== 'diagram' || !editDiagramName.trim()) {
			message.warning('Введите название диаграммы');
			return;
		}
		setEditDiagramLoading(true);
		const {data, error} = await diagramsApi.update(selected.projectId, selected.entityId, {
			name: editDiagramName.trim(),
		});
		setEditDiagramLoading(false);
		if (error || !data) {
			message.error(error?.message || 'Не удалось сохранить диаграмму');
			return;
		}
		tree.renameSelected(data.name);
		setEditDiagramOpen(false);
		message.success('Диаграмма обновлена');
	};

	const handleDelete = () => {
		if (!selected) return;
		if (selected.nodeType === 'project' && !canDeleteProject(selected.role)) return;
		if (selected.nodeType !== 'project' && !canEditContent(selected.role)) return;
		const titles = {
			project: 'Удалить проект',
			folder: 'Удалить папку',
			diagram: 'Удалить диаграмму',
		};
		const contents = {
			project: 'Удалить проект и всё его содержимое? Это действие необратимо.',
			folder: 'Удалить папку вместе с вложенными папками и диаграммами? Это действие необратимо.',
			diagram: 'Удалить диаграмму? Это действие необратимо.',
		};
		Modal.confirm({
			title: titles[selected.nodeType],
			content: contents[selected.nodeType],
			okText: 'Удалить',
			okType: 'danger',
			cancelText: 'Отмена',
			onOk: async () => {
				setDeleting(true);
				try {
					if (selected.nodeType === 'project') {
						const {error} = await projectsApi.delete(selected.projectId);
						if (error) {
							message.error(error.message || 'Не удалось удалить проект');
							return;
						}
						message.success('Проект удалён');
					} else if (selected.nodeType === 'folder') {
						const {error} = await foldersApi.delete(selected.projectId, selected.entityId);
						if (error) {
							message.error(error.message || 'Не удалось удалить папку');
							return;
						}
						message.success('Папка удалена');
					} else {
						const {error} = await diagramsApi.delete(selected.projectId, selected.entityId);
						if (error) {
							message.error(error.message || 'Не удалось удалить диаграмму');
							return;
						}
						message.success('Диаграмма удалена');
					}
					tree.dropNode(selected.key);
				} finally {
					setDeleting(false);
				}
			},
		});
	};

	const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		e.target.value = '';
		if (!file || !selected || selected.nodeType === 'diagram' || !canEditContent(selected.role)) return;
		if (!file.name.toLowerCase().endsWith('.xml')) {
			message.warning('Выберите файл ArchiMate XML (.xml)');
			return;
		}
		setImportLoading(true);
		try {
			const xml = await file.text();
			const name = file.name.replace(/\.xml$/i, '') || 'Импортированная диаграмма';
			const created = await tree.importDiagram(name, xml);
			if (created) {
				navigate(`/projects/${created.projectId}/diagrams/${created.id}`, {
					state: {diagramName: created.name ?? name},
				});
			}
		} catch {
			message.error('Не удалось прочитать файл');
		} finally {
			setImportLoading(false);
		}
	};

	return (
		<>
			<Breadcrumb
				style={{marginBottom: 16}}
				items={[
					{
						title: (
							<Link to="/">
								<HomeOutlined />
							</Link>
						),
					},
					{title: 'Проекты'},
				]}
			/>
			<Title level={4} style={{margin: '0 0 24px 0'}}>
				Проекты
			</Title>
			<Card style={{borderRadius: borderRadiusLG}}>
				<div className="workspace-page-toolbar">
					<WorkspaceSearch onSelect={handleSearchSelect} />
					<WorkspaceToolbar
						canAdd={tree.canAdd}
						canEdit={tree.canEdit}
						canDelete={tree.canDelete}
						onCreateProject={() => setProjectModalOpen(true)}
						onAddFolder={() => {
							setFolderName('');
							setFolderModalOpen(true);
						}}
						onAddDiagram={() => {
							setDiagramName('');
							setDiagramModalOpen(true);
						}}
						onImport={() => fileInputRef.current?.click()}
						onEdit={handleEdit}
						onDelete={handleDelete}
						importLoading={importLoading}
						deleting={deleting}
					/>
				</div>
				<input
					ref={fileInputRef}
					type="file"
					accept=".xml"
					style={{display: 'none'}}
					onChange={handleFileChange}
				/>
				{tree.loading ? (
					<Skeleton active paragraph={{rows: 6}} />
				) : tree.treeData.length === 0 ? (
					<Text type="secondary">Нет проектов. Создайте первый проект.</Text>
				) : (
					<WorkspaceTree
						treeData={tree.treeData}
						loadedKeys={tree.loadedKeys}
						expandedKeys={tree.expandedKeys}
						selectedKeys={tree.selectedKeys}
						onLoadData={tree.loadData}
						onLoadedKeysChange={tree.setLoadedKeys}
						onExpand={tree.setExpandedKeys}
						onSelect={tree.setSelectedKeys}
						onOpenDiagram={openDiagram}
						onAddFolder={() => {
							setFolderName('');
							setFolderModalOpen(true);
						}}
						onAddDiagram={() => {
							setDiagramName('');
							setDiagramModalOpen(true);
						}}
						onImport={() => fileInputRef.current?.click()}
						onEdit={handleEdit}
						onDelete={handleDelete}
						onShare={node => {
							setShareTarget(node);
							setShareOpen(true);
						}}
						onMove={(dragNode, destParent) => void tree.moveNode(dragNode, destParent)}
					/>
				)}
			</Card>
			<CreateProjectModal
				modalOpen={projectModalOpen}
				setModalOpen={setProjectModalOpen}
				onCreated={tree.addProjectNode}
			/>
			<CreateFolderModal
				open={folderModalOpen}
				name={folderName}
				onNameChange={setFolderName}
				onOk={() => void handleCreateFolder()}
				onCancel={() => {
					setFolderModalOpen(false);
					setFolderName('');
				}}
				confirmLoading={folderLoading}
			/>
			<CreateDiagramModal
				open={diagramModalOpen}
				name={diagramName}
				onNameChange={setDiagramName}
				onOk={() => void handleCreateDiagram()}
				onCancel={() => {
					setDiagramModalOpen(false);
					setDiagramName('');
				}}
				confirmLoading={diagramLoading}
			/>
			<EditFolderModal
				open={editFolderOpen}
				name={editFolderName}
				onNameChange={setEditFolderName}
				onOk={() => void handleSaveFolder()}
				onCancel={() => setEditFolderOpen(false)}
				confirmLoading={editFolderLoading}
			/>
			<EditDiagramModal
				open={editDiagramOpen}
				name={editDiagramName}
				onNameChange={setEditDiagramName}
				onOk={() => void handleSaveDiagram()}
				onCancel={() => setEditDiagramOpen(false)}
				confirmLoading={editDiagramLoading}
			/>
			<EditProjectModal
				project={editProject}
				setProject={project => {
					setEditProject(project);
					tree.renameSelected(project.name, project.description);
				}}
				open={editProjectOpen}
				onClose={() => setEditProjectOpen(false)}
			/>
			<ShareProjectModal
				open={shareOpen}
				projectId={shareTarget?.nodeType === 'project' ? shareTarget.projectId : null}
				projectName={shareTarget?.nodeType === 'project' ? String(shareTarget.title ?? '') : undefined}
				actorRole={shareTarget?.role}
				onClose={() => {
					setShareOpen(false);
					setShareTarget(null);
				}}
			/>
			<Modal
				open={Boolean(accessNotice)}
				title="Доступ к проекту"
				okText="Понятно"
				cancelButtonProps={{style: {display: 'none'}}}
				onOk={() => setAccessNotice(null)}
				onCancel={() => setAccessNotice(null)}
			>
				{accessNotice?.alreadyMember
					? `У вас уже есть доступ к проекту «${accessNotice.projectName}» (${roleLabel(accessNotice.role)}).`
					: `Вам открыт доступ к проекту «${accessNotice?.projectName ?? ''}» с правами «${roleLabel(accessNotice?.role)}».`}
			</Modal>
		</>
	);
};

export default Workspace;
